import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/apiClient';

const VerifySubUserAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation state
  const [passwordFocused, setPasswordFocused] = useState(false);
  
  // Check if password meets minimum requirements
  const isPasswordValid = formData.password.length >= 8;
  
  // Check if token is present in URL
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setIsTokenValid(false);
      setError('Verification token is missing. Please check your invitation link.');
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password meets requirements
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.post('/auth/verify-sub-user-account', {
        token,
        password: formData.password
      });
      
      setSuccess(true);
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify account. The link may be invalid or expired.');
      if (err.response?.status === 400) {
        setIsTokenValid(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isTokenValid) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ minHeight: '100vh', padding: '2rem' }}>
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">Invalid Invitation Link</h2>
            <p className="text-red-600 mb-4">{error || 'The invitation link is invalid or has expired.'}</p>
            <div className="flex flex-col gap-2">
              <p className="text-gray-600 text-sm">
                Please contact your administrator to resend the invitation.
              </p>
              <Link to="/login" className="text-blue-600 hover:underline">
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center" style={{ minHeight: '100vh', padding: '3rem 1.5rem' }}>
      <div style={{ maxWidth: '28rem', margin: '0 auto', width: '100%' }}>
        <h2 className="text-3xl font-bold text-center mb-2">
          Set Your Password
        </h2>
        <p className="text-center mb-6 text-gray-600">
          Welcome! Please create your password to complete your account setup.
        </p>
      </div>

      <div style={{ maxWidth: '28rem', margin: '2rem auto 0', width: '100%' }}>
        <div className="card">
          {success ? (
            <div className="p-6 text-center">
              <div className="mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Account Activated Successfully!</h3>
              <p className="mb-6">Your account has been verified and your password has been set. You will be redirected to the login page to sign in.</p>
              <div className="mt-6">
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:underline"
                >
                  Go to login page
                </Link>
              </div>
            </div>
          ) : (
            <form className="form" onSubmit={handleSubmit}>
              {error && (
                <div style={{ backgroundColor: '#FEE2E2', padding: '1rem', borderRadius: '0.375rem', marginBottom: '1.5rem' }}>
                  <p style={{ color: '#B91C1C', fontSize: '0.875rem', fontWeight: '500' }}>{error}</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Create Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    className="form-input"
                    placeholder="Enter your password"
                  />
                </div>
                {(passwordFocused || formData.password) && (
                  <div className="mt-2 text-sm">
                    <p style={{ 
                      color: isPasswordValid ? 'var(--color-green-600)' : 'var(--color-gray-500)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: isPasswordValid ? 'var(--color-green-500)' : 'var(--color-gray-300)',
                        color: 'white',
                        textAlign: 'center',
                        lineHeight: '16px',
                        fontSize: '12px'
                      }}>
                        {isPasswordValid ? '✓' : ''}
                      </span>
                      Must be at least 8 characters
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Confirm your password"
                  />
                </div>
                {formData.confirmPassword && (
                  <div className="mt-2 text-sm">
                    <p style={{ 
                      color: formData.password === formData.confirmPassword ? 'var(--color-green-600)' : 'var(--color-red-600)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: formData.password === formData.confirmPassword ? 'var(--color-green-500)' : 'var(--color-red-500)',
                        color: 'white',
                        textAlign: 'center',
                        lineHeight: '16px',
                        fontSize: '12px'
                      }}>
                        {formData.password === formData.confirmPassword ? '✓' : '✗'}
                      </span>
                      {formData.password === formData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`btn text-smd btn-primary ${isLoading ? 'btn-disabled' : ''}`}
                  style={{ width: '100%' }}
                >
                  {isLoading ? 'Creating Account...' : 'Complete Account Setup'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifySubUserAccount; 